"use client";

import { SessionProvider } from "next-auth/react";
import type { Session } from "next-auth";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/shared/ThemeProvider";
import { TauriUpdater } from "@/components/shared/TauriUpdater";
import { UpdateNotification } from "@/components/shared/UpdateNotification";
import { Toaster } from "@/components/shared/Toaster";
import { toast } from "@/lib/toast";
import { installDevToolsShortcut } from "@/lib/tauri-bridge";
import MesureAudience from "@/components/shared/MesureAudience";

export function Providers({
  children,
  session,
}: {
  children: React.ReactNode;
  /** Session lue cote serveur par le layout racine. `null` = visiteur non
   *  connecte — c'est une reponse, pas une absence de reponse. La distinction
   *  compte : `undefined` ferait redemarrer next-auth en etat « loading ». */
  session: Session | null;
}) {
  // Install F12 → DevTools shortcut in Tauri
  useEffect(() => {
    installDevToolsShortcut();
  }, []);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        // Centralised mutation error reporting. Any useMutation that doesn't
        // pass its own `onError` will surface the error message via a toast,
        // so the user is never left wondering why a click did nothing.
        // A mutation can opt out by passing `meta: { silent: true }`.
        mutationCache: new MutationCache({
          onError: (error, _vars, _ctx, mutation) => {
            if (mutation.meta?.silent) return;
            const message =
              error instanceof Error ? error.message : "Erreur inconnue";
            toast.error(message);
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
          },
        },
      })
  );

  return (
    /**
     * ── POURQUOI `refetchOnWindowFocus` EST COUPÉ ────────────────────────
     * Par défaut, NextAuth redemande `/api/auth/session` à CHAQUE retour sur
     * l'onglet. Quand le serveur est momentanément absent — un déploiement
     * dure une trentaine de secondes — le proxy répond une page HTML, et le
     * client jette « ClientFetchError: Unexpected token '<' » en travers de
     * l'écran. C'est precisement le retour sur l'onglet APRÈS un déploiement
     * qui déclenche le cas, donc le plus frequent de tous.
     *
     * Ce que ça coûte, et pourquoi c'est acceptable ici :
     *   • la session initiale vient du SERVEUR (`await auth()` dans le layout
     *     racine), donc rien n'est « en attente » au montage et le libellé de
     *     repli « Utilisateur » n'apparaît pas ;
     *   • le rôle est relu en base à chaque rafraîchissement de jeton, donc une
     *     promotion ou une rétrogradation prend effet à la navigation suivante ;
     *   • toutes les routes d'API revérifient la session côté serveur : ce
     *     réglage n'accorde aucun droit, il évite un aller-retour d'affichage.
     */
    <SessionProvider session={session} refetchOnWindowFocus={false}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider>
            <TauriUpdater />
            <UpdateNotification />
            {children}
            <Toaster />
            <MesureAudience />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </SessionProvider>
  );
}
